import Seo from "../components/Seo.jsx";
import { Link } from "react-router-dom";
import { VIGENCIA } from "../data/fiscal.js";

const ACTUALIZADO = "17 de septiembre de 2026";

function Pagina({ titulo, descripcion, path, children }) {
  return (
    <>
      <Seo title={`${titulo} — tu-kit.com`} description={descripcion} path={path} />
      <article className="mx-auto w-full max-w-3xl py-8">
        <h1 className="text-3xl font-extrabold tracking-tight text-[color:var(--texto)]">{titulo}</h1>
        <p className="mt-2 text-2xs texto-3">Última actualización: {ACTUALIZADO}</p>
        <div className="mt-8 space-y-6 text-sm leading-relaxed texto-2">{children}</div>
      </article>
    </>
  );
}

const H2 = ({ children }) => <h2 className="mb-2 mt-8 text-lg font-bold text-[color:var(--texto)]">{children}</h2>;
const Li = ({ children }) => (
  <li className="flex gap-2">
    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[color:var(--acento)]" aria-hidden="true" />
    <span>{children}</span>
  </li>
);

export function AvisoPrivacidad() {
  return (
    <Pagina
      titulo="Aviso de privacidad"
      descripcion="Qué datos recoge tu-kit.com, quién los trata y cómo ejercer tus derechos ARCO."
      path="/aviso-de-privacidad"
    >
      <p>
        Este aviso explica qué información se recoge cuando usas tu-kit.com y qué se hace con ella.
        Está redactado conforme a la Ley Federal de Protección de Datos Personales en Posesión de los
        Particulares (LFPDPPP).
      </p>

      <H2>Lo más importante: tus cálculos no salen de tu navegador</H2>
      <p>
        Todas las calculadoras de este sitio se ejecutan enteramente en tu dispositivo. Los sueldos,
        montos de crédito, gastos, peso o cualquier otro dato que escribas en un formulario{" "}
        <strong className="text-[color:var(--texto)]">no se envían a ningún servidor nuestro</strong>, no se
        guardan y no se comparten. Cuando cierras la pestaña, desaparecen.
      </p>

      <H2>Qué sí se recoge</H2>
      <ul className="space-y-2">
        <Li>
          <strong className="text-[color:var(--texto)]">Publicidad (Google AdSense).</strong> Financiamos el
          sitio con anuncios. Google y sus socios pueden usar cookies e identificadores para mostrar
          y medir anuncios. Si aceptas, verás publicidad personalizada; si rechazas, seguirás viendo
          anuncios, pero no personalizados.
        </Li>
        <Li>
          <strong className="text-[color:var(--texto)]">Preferencias locales.</strong> Guardamos tu decisión
          sobre cookies en el almacenamiento local de tu navegador. Ese dato no viaja a ningún lado.
        </Li>
        <Li>
          <strong className="text-[color:var(--texto)]">Formulario de sugerencias.</strong> Si nos escribes por
          el buzón, tu nombre, tu correo (si lo proporcionas) y tu mensaje se envían a Formspree, que
          nos los reenvía. Los usamos únicamente para responderte y mejorar las herramientas.
        </Li>
        <Li>
          <strong className="text-[color:var(--texto)]">Tipos de cambio.</strong> El conversor de divisas
          consulta el servicio público open.er-api.com. Esa petición sólo pide las tasas del día: no
          incluye ningún dato tuyo.
        </Li>
      </ul>

      <H2>Cookies de terceros</H2>
      <p>
        Google, como proveedor externo, utiliza cookies para publicar anuncios. Puedes configurar tus
        preferencias de publicidad personalizada en{" "}
        <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" className="acento underline underline-offset-2">
          adssettings.google.com
        </a>{" "}
        o inhabilitarlas en{" "}
        <a href="https://www.aboutads.info" target="_blank" rel="noopener noreferrer" className="acento underline underline-offset-2">
          aboutads.info
        </a>
        . También puedes bloquear cookies desde la configuración de tu navegador; el sitio seguirá
        funcionando igual.
      </p>

      <H2>Tus derechos</H2>
      <p>
        Tienes derecho a acceder, rectificar, cancelar u oponerte al tratamiento de tus datos
        (derechos ARCO), así como a revocar tu consentimiento en cualquier momento. Para ejercerlos,
        escríbenos desde la <Link to="/contacto" className="acento underline underline-offset-2">página de contacto</Link>.
        Puedes cambiar tu decisión sobre cookies borrando los datos del sitio en tu navegador.
      </p>

      <H2>Cambios a este aviso</H2>
      <p>
        Si cambia la forma en que tratamos los datos, actualizaremos esta página y la fecha del
        encabezado.
      </p>
    </Pagina>
  );
}

export function Terminos() {
  return (
    <Pagina
      titulo="Términos y condiciones"
      descripcion="Condiciones de uso de las calculadoras de tu-kit.com y límites de responsabilidad."
      path="/terminos-y-condiciones"
    >
      <p>
        Al usar tu-kit.com aceptas estas condiciones. Si no estás de acuerdo con alguna, lo mejor es
        que no utilices el sitio.
      </p>

      <H2>Las calculadoras son orientativas</H2>
      <p>
        Las herramientas de este sitio ofrecen estimaciones basadas en fórmulas públicas y en datos
        oficiales vigentes al momento de su última revisión ({VIGENCIA.etiqueta}, revisado el{" "}
        {VIGENCIA.revisadoEl}).{" "}
        <strong className="text-[color:var(--texto)]">
          No constituyen asesoría fiscal, financiera, legal, laboral ni médica
        </strong>
        , y no sustituyen el cálculo oficial del SAT, del IMSS, del INFONAVIT, de tu patrón, de tu
        institución financiera ni el criterio de un profesional de la salud.
      </p>
      <p>
        Las leyes, tasas e índices cambian. Antes de tomar una decisión con consecuencias económicas
        o de salud, verifica los resultados con la fuente oficial o con un profesional.
      </p>

      <H2>Sin garantías</H2>
      <p>
        El sitio se ofrece «tal cual». Ponemos cuidado en la exactitud de los cálculos y publicamos
        la vigencia de los datos que usamos, pero no garantizamos que la información esté libre de
        errores ni que el servicio esté disponible sin interrupciones. El conversor de divisas
        depende de un servicio externo que puede fallar o dejar de actualizarse; cuando eso ocurre,
        la propia herramienta lo indica.
      </p>

      <H2>Limitación de responsabilidad</H2>
      <p>
        No nos hacemos responsables de daños o perjuicios derivados del uso de las herramientas o de
        decisiones tomadas a partir de sus resultados. La responsabilidad de verificar la información
        antes de actuar es de quien la utiliza.
      </p>

      <H2>Propiedad y uso</H2>
      <p>
        Puedes usar las calculadoras libremente, para fines personales o profesionales, y enlazarlas
        desde donde quieras. El diseño, los textos y el código del sitio son de sus autores.
      </p>

      <H2>Publicidad</H2>
      <p>
        El sitio se financia con publicidad de Google AdSense. Los anuncios los selecciona Google:
        no controlamos ni respaldamos los productos o servicios anunciados.
      </p>
    </Pagina>
  );
}

export function AcercaDe() {
  return (
    <Pagina
      titulo="Acerca de tu-kit.com"
      descripcion="Quiénes somos, cómo calculamos y de dónde salen los datos fiscales que usamos."
      path="/acerca-de"
    >
      <p>
        tu-kit.com es una colección de calculadoras gratuitas pensadas específicamente para México.
        Nació de una frustración concreta: casi todas las calculadoras que encuentras en internet
        están hechas para otros países, con otras tasas de impuesto, otras reglas laborales y otra
        moneda.
      </p>

      <H2>Cómo trabajamos</H2>
      <ul className="space-y-2">
        <Li>
          <strong className="text-[color:var(--texto)]">Todo se calcula en tu navegador.</strong> No hay
          servidor procesando tus datos porque no hace falta. Es más rápido y más privado.
        </Li>
        <Li>
          <strong className="text-[color:var(--texto)]">Las constantes fiscales están centralizadas.</strong>{" "}
          La UMA, la tarifa del ISR, las cuotas del IMSS y las tasas del INFONAVIT viven en un único
          archivo con su fecha de vigencia, no repartidas por el código. Así se actualizan de una vez
          y no se contradicen entre herramientas.
        </Li>
        <Li>
          <strong className="text-[color:var(--texto)]">Publicamos la vigencia.</strong> Cada calculadora dice
          de cuándo son los datos que usa. Si ves una fecha vieja, desconfía.
        </Li>
        <Li>
          <strong className="text-[color:var(--texto)]">Sin registro.</strong> Ninguna herramienta te pide crear
          una cuenta ni dejar tu correo para ver el resultado.
        </Li>
      </ul>

      <H2>De dónde salen los datos</H2>
      <p>
        La tarifa del ISR y el subsidio para el empleo provienen de las publicaciones del SAT en el
        Diario Oficial de la Federación. La UMA la publica el INEGI cada febrero. Las cuotas obrero
        del IMSS son las que establece la Ley del Seguro Social. Las tasas y topes de descuento del
        crédito de vivienda son los que aplica el INFONAVIT según el nivel de ingreso.
      </p>

      <H2>¿Falta alguna calculadora?</H2>
      <p>
        El buzón de propuestas está abierto y lo leemos.{" "}
        <Link to="/proponer-herramienta" className="acento underline underline-offset-2">
          Dinos qué necesitas calcular
        </Link>
        .
      </p>
    </Pagina>
  );
}

export function Contacto() {
  return (
    <Pagina
      titulo="Contacto"
      descripcion="Escríbenos para reportar un error, sugerir una calculadora o ejercer tus derechos ARCO."
      path="/contacto"
    >
      <p>
        La forma más rápida de llegar a nosotros es el buzón del sitio. Lo leemos todo, aunque no
        siempre podamos responder de inmediato.
      </p>

      <H2>Para qué escribir</H2>
      <ul className="space-y-2">
        <Li>
          <strong className="text-[color:var(--texto)]">Un resultado no cuadra.</strong> Cuéntanos qué
          calculadora, qué datos metiste y qué resultado esperabas. Los errores de cálculo tienen
          prioridad sobre todo lo demás.
        </Li>
        <Li>
          <strong className="text-[color:var(--texto)]">Un dato quedó desactualizado.</strong> Si cambió la UMA,
          la tarifa del ISR o alguna tasa y no lo hemos reflejado, avísanos.
        </Li>
        <Li>
          <strong className="text-[color:var(--texto)]">Falta una calculadora.</strong> Las propuestas guían lo
          que construimos después.
        </Li>
        <Li>
          <strong className="text-[color:var(--texto)]">Privacidad.</strong> Para ejercer tus derechos ARCO o
          pedir la eliminación de un mensaje que nos enviaste, escríbenos por el mismo buzón.
        </Li>
      </ul>

      <p className="pt-2">
        <Link
          to="/proponer-herramienta"
          className="inline-block rounded-lg bg-[color:var(--acento)] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[color:var(--acento)]"
        >
          Abrir el buzón
        </Link>
      </p>
    </Pagina>
  );
}
